import React, { useState, useContext, useEffect, useRef } from "react";
import { useIntl } from "gatsby-plugin-intl";
import { CSSTransition } from "react-transition-group";
import styled from "styled-components";
import { useStaticQuery, graphql } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import SizeTooltip from "./sizeTooltip";

import { GlobalDispatchContext, GlobalStateContext } from "../context";

import CloseIcon from "./closeIcon";
import KCReactPlayer from "./kcReactPlayer";
import { parseSKUFromProductImage } from "../../utils";

const StyledWrapper = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 500;
  display: ${(props) => (props.isShown ? "flex" : "none")};
  opacity: 1;
  overflow: scroll;
  flex-direction: column;
  align-items: stretch;
  background-color: ${(props) => props.theme.color.smoke};

  &.fade-enter {
    opacity: 0;
    display: flex;
  }

  &.fade-enter-active {
    opacity: 1;
    transition: 1s opacity;
  }

  &.fade-enter-done {
    opacity: 1;
    display: flex;
  }

  &.fade-exit {
    opacity: 1;
  }

  &.fade-exit-active {
    opacity: 0;
    transition: 0.3s opacity;
  }

  &.fade-exit-done {
    opacity: 0;
    display: none;
  }

  ${(props) => props.theme.responsive.desktop} {
    overflow: hidden;
    flex-direction: row;
  }
`;

const StyledPreview = styled.div`
  flex: 1;
  position: relative;
  width: 100%;
  ${(props) => props.theme.responsive.desktop} {
    width: 50%;
  }
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 999;
`;

const StyleOverlayRender = styled(GatsbyImage)`
    width: 100%;
    height: 100%:
    max-width: 30rem;
    opacity: ${(props) => (props.isSelected ? 1 : 0.5)};
    filter: ${(props) =>
      props.isSelected
        ? `sepia() saturate(10000%) hue-rotate(310deg)`
        : `none`};
`;

const StyledOverlayRenderWrapper = styled.div`
  position: absolute;
  display: flex;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 99;
  margin: auto;
  max-width: 30rem;
`;

const SizeOverlayRender = ({ img, isSelected, ...props }) => {
  return (
    <StyledOverlayRenderWrapper>
      <StyleOverlayRender
        isSelected={isSelected}
        imgStyle={{
          margin: 0,
          objectFit: "contain",
          objectPosition: "center center",
        }}
        image={img}
        alt="Render"
      />
    </StyledOverlayRenderWrapper>
  );
};

const StyledRender = styled(GatsbyImage)`
  width: 100%;
  margin: 0;
  height: 100%;
  max-width: 30rem;
  margin: auto;
  background-color: ${(props) => props.theme.color.smoke};
  ${(props) => props.theme.responsive.desktop} {
    &:hover {
      cursor: pointer;
    }
  }
`;

const Render = ({ img, ...props }) => {
  return (
    <>
      {img && (
        <StyledRender
          imgStyle={{
            margin: 0,
            objectFit: "contain",
            objectPosition: "center center",
          }}
          image={img}
          alt="render"
        />
      )}
    </>
  );
};

const StyledSidebar = styled.div`
  z-index: 20;
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: ${(props) => props.theme.color.white};
  font-family: ${(props) => props.theme.font.sans};
  padding: 1rem;
  ${(props) => props.theme.responsive.desktop} {
    flex-direction: column;
    width: 20rem;
    ${(props) => props.theme.shadow}
  }
`;

const StyledTitle = styled.div`
  font-weight: bold;
  font-size: 1.2rem;
  margin-top: 1.2rem;
`;

const StyledBody = styled.div`
  margin-top: 0.6rem;
  font-size: 0.9rem;
  line-height: 1.2rem;
`;

const StyledSizeButtonWrapper = styled.div`
  margin-top: 1.2rem;
  margin-bottom: 0.8rem;
`;

const StyledSizeButton = styled.div`
  border: 1px solid
    ${(props) =>
      props.isSelected
        ? props.theme.color.dimGrey
        : props.theme.color.lightGrey};
  padding: 0.8rem;
  margin-bottom: 0.4rem;
  border-radius: 2px;
  font-size: 0.9rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  &:hover {
    cursor: pointer;
    border-color: ${(props) => props.theme.color.dimGrey};
  }
`;

const StyledSizeTitle = styled.div`
  font-weight: 600;
  padding: 0.3rem 0.6rem;
  border-radius: 1px;
  background-color: ${(props) => props.theme.color.smoke};
`;
const StyledSizeSubtitle = styled.div`
  position: relative;
  margin-top: 0.4rem;
  color: ${(props) => props.theme.color.dimGrey};
`;

const StyledButtonWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: stretch;
  margin: 1rem 0 5rem 0;
`;
const StyledContactButton = styled.div`
  border-radius: 2px;
  background-color: ${(props) => props.theme.color.smoke};
  padding: 1rem;
  font-weight: 600;
  margin-right: 1rem;

  &:hover {
    cursor: pointer;
    opacity: 0.8;
  }
`;
const StyledSizingChart = styled.div`
  position: relative;
  font-weight: 600;
  color: ${(props) => props.theme.color.blue};
  margin: 1rem 0;

  &:hover {
    cursor: pointer;
    opacity: 0.8;
  }
`;
const ProductRender = styled.div``;

const SizePopup = ({ ...props }) => {
  const state = useContext(GlobalStateContext);
  const dispatch = useContext(GlobalDispatchContext);
  const isShown = state.isSizePopupShown;
  const sku = state.curSKU;
  const intl = useIntl();
  const [curHighlightedSize, setCurHighlightedSize] = useState(null);
  const [isSizeChartShown, setIsSizeChartShown] = useState(false);

  const { modelsRender, small, medium, large } = useStaticQuery(graphql`
    {
      modelsRender: file(relativePath: { eq: "Sizing/models.png" }) {
        childImageSharp {
          gatsbyImageData(width: 900, layout: CONSTRAINED)
        }
      }
      small: file(relativePath: { eq: "Sizing/small.png" }) {
        childImageSharp {
          gatsbyImageData(width: 900, layout: CONSTRAINED)
        }
      }
      medium: file(relativePath: { eq: "Sizing/medium.png" }) {
        childImageSharp {
          gatsbyImageData(width: 900, layout: CONSTRAINED)
        }
      }
      large: file(relativePath: { eq: "Sizing/large.png" }) {
        childImageSharp {
          gatsbyImageData(width: 900, layout: CONSTRAINED)
        }
      }
    }
  `);

  const handlerClosePopup = () => {
    dispatch({
      type: "SET_SIZE_POPUP_SHOWN",
      isShown: false,
    });
  };

  const handleUpdateSize = (size) => {
    dispatch({
      type: "UPDATE_CUR_FRAME_SIZE",
      sku: sku,
      size: size,
    });
  };

  const handleShowChat = () => {
    document.querySelector("#crisp-chatbox > div > a").click();
  };

  let curSize = curHighlightedSize || "M";

  return (
    <CSSTransition in={isShown} timeout={1000} classNames="fade">
      <StyledWrapper className="fade" isShown={isShown}>
        <StyledPreview>
          <Render img={modelsRender.childImageSharp.gatsbyImageData} />
          <SizeOverlayRender
            isSelected={curSize === "L"}
            img={large.childImageSharp.gatsbyImageData}
          />
          <SizeOverlayRender
            isSelected={curSize === "M"}
            img={medium.childImageSharp.gatsbyImageData}
          />
          <SizeOverlayRender
            isSelected={curSize === "S"}
            img={small.childImageSharp.gatsbyImageData}
          />
          <CloseButtonWrapper>
            <CloseIcon handlerClick={() => handlerClosePopup()} size={1} />
          </CloseButtonWrapper>
        </StyledPreview>
        <StyledSidebar>
          <StyledTitle>
            {intl.formatMessage({
              id: `product.sizes.title`,
            })}
          </StyledTitle>
          <StyledBody>
            {intl.formatMessage({
              id: `product.sizes.description`,
            })}
          </StyledBody>
          <StyledSizeButtonWrapper>
            {/* {curFrame.sizes.map((size) => (
              <StyledSizeButton
                key={size}
                onClick={() => handleUpdateSize(size)}
                isSelected={curFrame.selectedSize === size}
                onMouseEnter={() => setCurHighlightedSize(size)}
                onMouseLeave={() => setCurHighlightedSize(null)}
              >
                <StyledSizeTitle>
                  {intl.formatMessage({
                    id: `product.sizes.${size}.name`,
                  })}
                </StyledSizeTitle>
                <StyledSizeSubtitle>
                  {intl.formatMessage({
                    id: `product.sizes.${size}.description`,
                  })}
                </StyledSizeSubtitle>
              </StyledSizeButton>
            ))} */}
          </StyledSizeButtonWrapper>
          <StyledButtonWrapper>
            <StyledContactButton onClick={() => handleShowChat()}>
              {intl.formatMessage({
                id: `product.sizes.contact`,
              })}
            </StyledContactButton>
            <StyledSizingChart
              onClick={() => setIsSizeChartShown(!isSizeChartShown)}
            >
              {intl.formatMessage({
                id: `product.sizes.chartTitle`,
              })}
              <SizeTooltip isHidden={!isSizeChartShown} />
            </StyledSizingChart>
          </StyledButtonWrapper>
          <ProductRender />
        </StyledSidebar>
      </StyledWrapper>
    </CSSTransition>
  );
};

export default SizePopup;
